/**
 * Stock Context - Manages global stock-related state
 * Centralizes state that was previously scattered across App.js
 */
import { createContext, useContext, useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { logger } from '../utils/logger';
import axios from 'axios';
import { toast } from 'sonner';
import { CHART_PERIODS } from '../config/chartPeriods';
import { 
  getCachedStockInfo, 
  cacheStockInfo, 
  getCachedStockHistory, 
  cacheStockHistory,
  cleanExpiredCache 
} from '../utils/localCache';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StockContext = createContext(null);

export const useStock = () => {
  const context = useContext(StockContext);
  if (!context) {
    throw new Error('useStock must be used within a StockProvider');
  }
  return context;
};

export const StockProvider = ({ children }) => {
  // 📱 PWA STATE RESTORATION: leer ticker y período persistidos para que al
  // volver de segundo plano en Android (donde Chrome mata el proceso) el
  // usuario aterrice en el mismo stock que estaba viendo.
  const [ticker, setTicker] = useState(() => {
    try {
      const saved = localStorage.getItem('kapital_last_ticker');
      if (saved && /^[A-Z0-9.\-^]{1,10}$/.test(saved)) return saved;
    } catch { /* ignore */ }
    return 'NVDA';
  });
  const [searchInput, setSearchInput] = useState(() => {
    try {
      const saved = localStorage.getItem('kapital_last_ticker');
      if (saved && /^[A-Z0-9.\-^]{1,10}$/.test(saved)) return saved;
    } catch { /* ignore */ }
    return 'NVDA';
  });
  const [stockInfo, setStockInfo] = useState(null);
  const [stockHistory, setStockHistory] = useState([]);
  // Bandera: intervalo intradía (5m/15m/1H/4H) sin datos disponibles
  // (mercado cerrado, fin de semana o ticker no soportado por yfinance).
  // El UI la usa para mostrar un aviso en lugar de un gráfico vacío.
  const [intradayNoData, setIntradayNoData] = useState(false);
  
  // Loading state
  const [loading, setLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  
  // Detailed loading state - secciones
  const [currentLoadingSection, setCurrentLoadingSection] = useState(null);
  const [completedSections, setCompletedSections] = useState([]);
  
  // Update state
  const [lastUpdate, setLastUpdate] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  
  // Chart state (persistido en localStorage para PWA restore)
  const [chartPeriod, setChartPeriod] = useState(() => {
    try {
      const savedId = localStorage.getItem('kapital_chart_period');
      if (savedId) {
        const found = CHART_PERIODS.find(p => (p.id || p.label) === savedId);
        if (found) return found;
      }
    } catch { /* ignore */ }
    return CHART_PERIODS.find(p => p.label === '1M') || CHART_PERIODS[0];
  });
  
  // Portfolio state
  const [portfolioTickers, setPortfolioTickers] = useState([]);
  
  // Visibility state
  const [isTabVisible, setIsTabVisible] = useState(true);
  
  // Refs
  const isRefreshing = useRef(false);
  const lastVisibilityRefresh = useRef(Date.now());
  const timerIntervalRef = useRef(null);
  // Última petición solicitada (ticker|period|interval|aggregate). Sirve para
  // descartar respuestas OBSOLETAS: si el usuario cambia de activo mientras una
  // petición anterior sigue en vuelo, esa respuesta antigua NO debe pisar el
  // gráfico del activo nuevo.
  const latestRequestRef = useRef(null);
  
  // Helper para marcar sección como completada
  const completeSection = useCallback((section) => {
    setCompletedSections(prev => [...new Set([...prev, section])]);
    setCurrentLoadingSection(null);
  }, []); // No dependencies needed - uses functional setState updates
  
  // Helper para resetear progreso detallado
  const resetDetailedProgress = useCallback(() => {
    setCurrentLoadingSection(null);
    setCompletedSections([]);
  }, []); // No dependencies needed - uses setState directly

  // Fetch stock data with optimized caching
  const fetchStockData = useCallback(async (newTicker, period, interval, silentUpdate = false, aggregate = 1) => {
    // No abandonar NUNCA una acción EXPLÍCITA del usuario (cambiar de activo o de
    // período) aunque haya un auto-refresh en curso. Antes la guarda descartaba
    // el clic del usuario si isRefreshing estaba activo -> el gráfico se quedaba
    // en el activo anterior y había que refrescar a mano. Solo evitamos apilar
    // refrescos SILENCIOSOS (auto-refresh) cuando ya hay uno en marcha.
    if (isRefreshing.current && silentUpdate) return;
    isRefreshing.current = true;
    // Marcar esta como la petición vigente. Cualquier respuesta que llegue de una
    // petición anterior (isStale) se descartará antes de tocar el estado.
    const requestKey = `${newTicker}|${period}|${interval}|${aggregate}`;
    latestRequestRef.current = requestKey;
    const isStale = () => latestRequestRef.current !== requestKey;
    // Codificar el ticker para la URL: soporta índices/futuros con caracteres
    // especiales (^VIX, ES=F, EURUSD=X). FastAPI decodifica el path param.
    const encTicker = encodeURIComponent(newTicker);
    
    try {
      if (!silentUpdate) {
        setLoading(true);
        setLoadingProgress(10);
        resetDetailedProgress();
        setCurrentLoadingSection('stockInfo');
      }
      
      // Cookie httpOnly se envía automáticamente con axios
      
      // ==========================================
      // CACHÉ AGRESIVA: Intentar obtener de caché primero
      // ==========================================
      const cachedInfo = getCachedStockInfo(newTicker);
      const cachedHistory = getCachedStockHistory(newTicker, period, interval, aggregate);
      
      // Si tenemos datos en caché y no es actualización silenciosa, usarlos inmediatamente
      if (cachedInfo && !silentUpdate) {
        setStockInfo(cachedInfo);
        if (!silentUpdate) {
          setLoadingProgress(30);
          completeSection('stockInfo');
          setCurrentLoadingSection('stockHistory');
        }
      }
      
      if (cachedHistory && !silentUpdate) {
        setStockHistory(cachedHistory);
        if (!silentUpdate) {
          setLoadingProgress(60);
          completeSection('stockHistory');
          setCurrentLoadingSection('chart');
        }
      }
      
      // Si tenemos ambos en caché, mostrar datos instantáneamente
      // pero seguir actualizando en segundo plano
      const hasBothCached = cachedInfo && cachedHistory;
      if (hasBothCached && !silentUpdate) {
        setLoadingProgress(100);
        completeSection('chart');
        completeSection('indicators');
        completeSection('complete');
        setTimeout(() => {
          setLoading(false);
          setLoadingProgress(0);
        }, 200);
      }
      
      // ==========================================
      // FETCH EN SEGUNDO PLANO (siempre actualizar)
      // ==========================================
      
      // Fetch info
      if (!silentUpdate && !cachedInfo) {
        setCurrentLoadingSection('stockInfo');
      }
      
      // PERF (jun 2026): lanzar info e historial EN PARALELO. Antes iban en serie
      // (await info -> await history), así que las velas esperaban innecesariamente
      // a que terminara la info. Con el rate-limit de yfinance eso duplicaba el
      // tiempo de cambio de activo. Ahora ambas peticiones vuelan a la vez y el
      // tiempo total es ~max(info, history) en lugar de la suma.
      const aggParam = (aggregate && aggregate > 1) ? `&aggregate=${aggregate}` : '';
      const historyPromise = axios.get(
        `${API}/stock/${encTicker}/history?period=${period}&interval=${interval}${aggParam}`,
        { timeout: 30000 }
      );
      // Evita el warning de "unhandled rejection" mientras esperamos la info;
      // la gestión real del error se hace más abajo con `await historyPromise`.
      historyPromise.catch(() => {});
      
      const infoResponse = await axios.get(`${API}/stock/${encTicker}/info`, { 
        timeout: 30000 
      });
      
      // Descartar si el usuario ya cambió de activo/periodo mientras esperábamos.
      if (isStale()) return;
      
      // Only update if we got valid data
      if (infoResponse.data && infoResponse.data.ticker) {
        // Guardar en caché
        cacheStockInfo(newTicker, infoResponse.data);
        
        setStockInfo(prev => {
          // Only update if data changed (prevent unnecessary re-renders)
          if (JSON.stringify(prev) === JSON.stringify(infoResponse.data)) {
            return prev;
          }
          return infoResponse.data;
        });
      }
      
      if (!silentUpdate && !hasBothCached) {
        setLoadingProgress(50);
        completeSection('stockInfo');
        setCurrentLoadingSection('stockHistory');
      }
      
      // Fetch history
      // FIX (job 6cdb8552): el backend devuelve 404 para intradía cuando el mercado del ticker
      // está cerrado (p. ej. acciones USA fuera de horario NY). Antes el 404 lanzaba y saltaba al
      // catch externo, pisando el fallback a 1mo/1d -> el gráfico quedaba vacío y no cambiaba de
      // activo. Ahora tragamos el 404 (historyData=[]) para que el fallback de abajo actúe.
      let historyResponse;
      let historyData = [];
      try {
        historyResponse = await historyPromise;
        // Extract data array from response (API returns {ticker: ..., data: [...]})
        historyData = historyResponse.data?.data || historyResponse.data;
      } catch (e) {
        if (e?.response?.status !== 404) throw e;
      }
      
      // FALLBACK: If period is intraday (1d/5d/intraday intervals) and we get less than 2 data points,
      // dejamos historyData vacío para que el UI muestre aviso de "sin datos / mercado cerrado"
      // en lugar de pintar un gráfico en blanco. Solo hacemos fallback silencioso a 1mo/1d
      // en los períodos legacy 1D/5D (comportamiento previo).
      const isIntradayInterval = ['5m', '15m', '30m', '1h'].includes(interval);
      const isLegacyIntraday = (period === '1d' || period === '5d') && !isIntradayInterval;
      const isNewIntraday = isIntradayInterval && (aggregate === 1 || aggregate > 1);
      const noData = !Array.isArray(historyData) || historyData.length < 2;
      const needsFallback = isLegacyIntraday && noData;
      // Los nuevos intradía (5m/15m/1H/4H) marcan bandera para que el UI muestre aviso
      const intradayEmpty = isNewIntraday && noData;
      
      if (needsFallback) {
        console.log(`[StockContext] No data for ${newTicker} with ${period}, falling back to 1mo daily`);
        try {
          historyResponse = await axios.get(
            `${API}/stock/${encTicker}/history?period=1mo&interval=1d`,
            { timeout: 30000 }
          );
          historyData = historyResponse.data?.data || historyResponse.data;
          
          // Update the chart period to reflect the fallback
          if (Array.isArray(historyData) && historyData.length >= 2) {
            console.log(`[StockContext] Fallback successful, setting period to 1M`);
            const oneMonth = CHART_PERIODS.find(p => p.label === '1M');
            if (oneMonth) setChartPeriod(oneMonth);
          }
        } catch (fallbackError) {
          console.log(`[StockContext] Fallback to 1mo also failed for ${newTicker}`);
        }
      }
      
      if (Array.isArray(historyData) && historyData.length > 0) {
        // Descartar si el usuario ya cambió de activo mientras esperábamos.
        if (isStale()) return;
        // Guardar en caché
        cacheStockHistory(newTicker, period, interval, historyData, aggregate);
        setStockHistory(historyData);
        // Datos disponibles → limpiar aviso intradía
        setIntradayNoData(false);
        // NOTA: NO reescribimos chartPeriod aquí. El botón exacto ya fue
        // aplicado optimísticamente por App.js en handlePeriodChange con el
        // objeto que pulsó el usuario (con su `id` único). Re-resolver por
        // period+interval+aggregate provoca colisiones entre 1D/5m y 5D/5m
        // (comparten period/interval) — de ahí que 1D "saltase" a 5m.
      } else if (intradayEmpty) {
        // Intradía sin datos: no borramos el historial actual (para no romper
        // el chart previo), solo marcamos la bandera y avisamos. El botón
        // sigue reflejando la elección del usuario (aplicada en App.js).
        setIntradayNoData(true);
        toast.info(
          `Sin datos intradía para ${newTicker} (${interval}). El mercado puede estar cerrado o el ticker no tiene datos en este intervalo.`,
          { duration: 4000 }
        );
      } else {
        setIntradayNoData(false);
      }
      
      if (!silentUpdate && !hasBothCached) {
        setLoadingProgress(75);
        completeSection('stockHistory');
        setCurrentLoadingSection('chart');
        
        // NOTA: El progreso real del gráfico ahora se reporta desde InteractiveChart
        // usando useComponentLoading de LoadingContext - NO simular con setTimeout
        setLoadingProgress(100);
        completeSection('chart');
        completeSection('indicators');
        completeSection('complete');
      }
      
      setLastUpdate(new Date());
      
    } catch (error) {
      logger.error('Error fetching stock data:', error);
      // Don't clear existing data on error during silent updates
      if (!silentUpdate) {
        setLoadingProgress(0);
      }
    } finally {
      if (!silentUpdate) {
        setTimeout(() => {
          setLoading(false);
          setLoadingProgress(0);
        }, 300);
      }
      isRefreshing.current = false;
    }
  }, [completeSection, resetDetailedProgress]);

  // Limpiar caché expirada cada 5 minutos
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      cleanExpiredCache();
    }, 5 * 60 * 1000);
    
    return () => clearInterval(cleanupInterval);
  }, []);

  // Timer effect for loading progress countdown
  useEffect(() => {
    if (loading && loadingProgress > 0 && loadingProgress < 100) {
      if (!timerIntervalRef.current) {
        const estimatedTotal = 15;
        const remainingPercent = (100 - loadingProgress) / 100;
        setElapsedSeconds(Math.ceil(estimatedTotal * remainingPercent));
        
        timerIntervalRef.current = setInterval(() => {
          setElapsedSeconds(prev => {
            const newTime = prev - 1;
            if (newTime <= 1 && loadingProgress < 90) {
              const remaining = (100 - loadingProgress) / 100;
              return Math.max(3, Math.ceil(8 * remaining));
            }
            return Math.max(1, newTime);
          });
        }, 1000);
      }
    } else if (loadingProgress === 100 || !loading) {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
      setElapsedSeconds(0);
    }
    
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, [loading, loadingProgress]);

  // Visibility change handler
  useEffect(() => {
    const handleVisibilityChange = () => {
      const visible = document.visibilityState === 'visible';
      setIsTabVisible(visible);
      
      if (visible) {
        logger.log('📱 Pestaña visible - Reanudando actualizaciones');
      } else {
        logger.log('📱 Pestaña oculta - Pausando actualizaciones...');
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // 📱 PWA STATE PERSISTENCE: guarda ticker y período cada vez que cambian.
  // Al restaurar el proceso tras suspensión en Android, el usuario vuelve al
  // mismo stock y timeframe que estaba viendo.
  useEffect(() => {
    try {
      if (ticker) localStorage.setItem('kapital_last_ticker', ticker);
    } catch { /* ignore */ }
  }, [ticker]);

  useEffect(() => {
    try {
      if (chartPeriod?.id || chartPeriod?.label) {
        localStorage.setItem('kapital_chart_period', chartPeriod.id || chartPeriod.label);
      }
    } catch { /* ignore */ }
  }, [chartPeriod]);

  // Load portfolio tickers
  const loadPortfolioTickers = useCallback(async () => {
    try {
      // Cookie httpOnly se envía automáticamente
      const response = await axios.get(`${API}/user/investments`);
      
      const investments = response.data?.investments || {};
      const tickers = Object.keys(investments);
      setPortfolioTickers(tickers);
    } catch (error) {
      logger.error('Error loading portfolio tickers:', error);
    }
  }, []);

  // Memoizar el objeto value para evitar re-renders innecesarios
  const value = useMemo(() => ({
    // State
    ticker, setTicker,
    searchInput, setSearchInput,
    stockInfo, setStockInfo,
    stockHistory, setStockHistory,
    intradayNoData, setIntradayNoData,
    loading, setLoading,
    loadingProgress, setLoadingProgress,
    elapsedSeconds, setElapsedSeconds,
    lastUpdate, setLastUpdate,
    autoRefresh, setAutoRefresh,
    chartPeriod, setChartPeriod,
    portfolioTickers, setPortfolioTickers,
    isTabVisible, setIsTabVisible,
    
    // Detailed loading state
    currentLoadingSection,
    completedSections,
    completeSection,
    resetDetailedProgress,
    
    // Refs
    isRefreshing,
    lastVisibilityRefresh,
    
    // Actions
    fetchStockData,
    loadPortfolioTickers,
    
    // Constants
    CHART_PERIODS,
  }), [
    ticker, searchInput, stockInfo, stockHistory, intradayNoData, loading, loadingProgress,
    elapsedSeconds, lastUpdate, autoRefresh, chartPeriod, portfolioTickers,
    isTabVisible, currentLoadingSection, completedSections, completeSection,
    resetDetailedProgress, fetchStockData, loadPortfolioTickers
  ]);

  return (
    <StockContext.Provider value={value}>
      {children}
    </StockContext.Provider>
  );
};

export default StockContext;
